import type { Writable } from "svelte/store";
import { FS } from "./fs";
import type { GofileConfig } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { throwIf } from "./util";

// https://gofile.io/api
// The Gofile API and its storage servers answer other origins, so the browser
// calls them directly and the token never reaches our server. The token goes
// in the Authorization header. The calls follow rclone's gofile backend.
//
// Gofile knows folders and files by id and has no call that takes a path. A
// path is walked one folder at a time and the folder ids we learn are
// remembered. A folder can hold several entries with the same name: the first
// one listed is the one a path means.
//
// Gofile gives most of the API to premium accounts only.

export const gofileAPI = "https://api.gofile.io";
export const gofileUploadURL = "https://upload.gofile.io/uploadfile";

// as in rclone
const pageSize = 1000;
const maxNameLen = 255;

export const prop_gofile_md5 = "gofile_md5";
export const prop_gofile_mime_type = "gofile_mime_type";

export interface GofileItem {
  id?: string;
  type?: string;
  name?: string;
  size?: number;
  // seconds since epoch
  createTime?: number;
  modTime?: number;
  // where the content of a file is, for a request with the token
  link?: string;
  md5?: string;
  mimetype?: string;
}

export interface GofileAccount {
  rootFolderID: string;
  email: string;
}

class GofileError extends Error {
  // what Gofile calls the error, like error-notFound
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

function enc(s: string): string {
  return encodeURIComponent(s);
}

function splitPath(path: string): string[] {
  return path.split("/").filter((s) => s != "");
}

function dirKey(parts: string[]): string {
  return parts.join("/");
}

function isFolder(it: GofileItem): boolean {
  return it.type == "folder";
}

function findItem(items: GofileItem[], name: string, folder: boolean): GofileItem | undefined {
  return items.find((it) => it.id && it.name == name && isFolder(it) == folder && (folder || it.type == "file"));
}

// Refuses the names rclone does not send to Gofile as they are.
export function gofileCheckName(name: string): void {
  throwIf(name == "", "missing name");
  throwIf(name.length > maxNameLen, `a name cannot be longer than ${maxNameLen} characters`);
  let bad = /[<>:"\\|?*!\x00-\x1f\x7f]/.test(name) || name.startsWith(".") || name.endsWith(".");
  throwIf(bad, 'a name cannot contain < > : " \\ | ? * ! or start or end with a dot');
}

class FileSystemGofile extends FS {
  supportsDirRename = true;

  // folder ids by path, without slashes at the ends
  dirIDs = new Map<string, string>();

  constructor(config: GofileConfig) {
    super(config);
  }

  get token(): string {
    return (this.config as GofileConfig).token;
  }

  get rootID(): string {
    return (this.config as GofileConfig).rootFolderID;
  }

  // Everything in a folder, however many pages that takes.
  async list(folderID: string): Promise<GofileItem[]> {
    let items: GofileItem[] = [];
    for (let page = 1; ; page++) {
      let js = await gfCall(this.token, "GET", `/contents/${enc(folderID)}?page=${page}&pageSize=${pageSize}`);
      let children = js.data?.children;
      if (children && typeof children == "object") {
        items.push(...(Object.values(children) as GofileItem[]));
      }
      if (!js.metadata?.hasNextPage) {
        return items;
      }
    }
  }

  // Remembers the folders of the folder at parts, all of them: what we knew
  // about folders that are gone or were replaced is forgotten.
  remember(parts: string[], items: GofileItem[]) {
    let prefix = parts.length > 0 ? dirKey(parts) + "/" : "";
    let fresh = new Map<string, string>();
    for (let it of items) {
      if (isFolder(it) && it.id && it.name && !fresh.has(it.name)) {
        fresh.set(it.name, it.id);
      }
    }
    let old = new Map(this.dirIDs);
    for (let k of old.keys()) {
      if (!k.startsWith(prefix)) {
        continue;
      }
      // the folder of this listing that k is, or is under
      let child = k.substring(prefix.length).split("/")[0];
      if (fresh.get(child) != old.get(prefix + child)) {
        this.dirIDs.delete(k);
      }
    }
    for (let [name, id] of fresh) {
      this.dirIDs.set(prefix + name, id);
    }
  }

  async mkdir(parentID: string, name: string): Promise<string> {
    gofileCheckName(name);
    let js = await gfCall(this.token, "POST", "/contents/createFolder", {
      parentFolderId: parentID,
      folderName: name,
    });
    let id = js.data?.id;
    throwIf(!id, "Gofile did not create the folder");
    return id as string;
  }

  // Finds the id of the folder at parts. With create it makes the folders
  // that are missing.
  async resolveDir(parts: string[], create: boolean): Promise<string> {
    let id = this.rootID;
    // start at the deepest folder we remember
    let start = 0;
    for (let n = parts.length; n > 0; n--) {
      let known = this.dirIDs.get(dirKey(parts.slice(0, n)));
      if (known) {
        id = known;
        start = n;
        break;
      }
    }
    for (let i = start; i < parts.length; i++) {
      let items = await this.list(id);
      this.remember(parts.slice(0, i), items);
      let found = findItem(items, parts[i], true);
      if (found) {
        id = found.id!;
        continue;
      }
      throwIf(!create, `folder not found: /${parts.slice(0, i + 1).join("/")}`);
      id = await this.mkdir(id, parts[i]);
      this.dirIDs.set(dirKey(parts.slice(0, i + 1)), id);
    }
    return id;
  }

  // Lists the folder that holds the entry at parts.
  async parentOf(parts: string[], create: boolean): Promise<{ id: string; items: GofileItem[] }> {
    let dir = parts.slice(0, -1);
    let id = await this.resolveDir(dir, create);
    let items = await this.list(id);
    this.remember(dir, items);
    return { id, items };
  }

  // A folder id we remember may be of a folder that is gone. When Gofile does
  // not find something, forget the ids and try once more from the top.
  async fresh<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      let notFound = e instanceof GofileError && e.code == "error-notFound";
      if (!notFound || this.dirIDs.size == 0) {
        throw e;
      }
      this.dirIDs.clear();
      return await fn();
    }
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parts = splitPath(dirPath);
      let items = await this.fresh(async () => this.list(await this.resolveDir(parts, false)));
      this.remember(parts, items);
      return FsDir.fromWire(dirPath, gofileItemsToWire(items));
    });
  }

  async findFile(path: string): Promise<GofileItem> {
    let parts = splitPath(path);
    throwIf(parts.length == 0, "missing file name");
    return this.fresh(async () => {
      let parent = await this.parentOf(parts, false);
      let f = findItem(parent.items, parts[parts.length - 1], false);
      throwIf(!f, `file not found: ${path}`);
      return f!;
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the token. Fetch the file
    // and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let f = await this.findFile(path);
    throwIf(!f.link, "Gofile gave no address for the file");
    // the token is for Gofile only
    let host = "";
    try {
      let u = new URL(f.link!);
      host = u.protocol == "https:" ? u.hostname : "";
    } catch {
      // host stays empty
    }
    throwIf(host != "gofile.io" && !host.endsWith(".gofile.io"), "Gofile gave an address that is not its own");
    let xhr = await gfSend("GET", f.link!, this.token, null, "blob", (loaded, total, durMs) => {
      httpProgress(loaded, total || f.size || 0, durMs);
    });
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let parts = splitPath(path);
    throwIf(parts.length == 0 || path.endsWith("/"), "missing file name");
    let name = parts[parts.length - 1];
    gofileCheckName(name);

    let target = await this.fresh(async () => {
      let parent = await this.parentOf(parts, true);
      return { folderID: parent.id, old: findItem(parent.items, name, false) };
    });

    let form = new FormData();
    form.append("folderId", target.folderID);
    if (file instanceof File) {
      // seconds: Gofile keeps no more than that
      form.append("modTime", String(Math.floor(file.lastModified / 1000)));
    }
    // the name of the path, not the one the file came with
    form.append("file", new File([file], name, { type: file.type }));
    let xhr = await gfSend("POST", gofileUploadURL, this.token, form, "text", (loaded, _total, durMs) => {
      httpProgress(Math.min(loaded, file.size), file.size, durMs);
    });
    let js = parseJSON(xhr.responseText);
    if (js.status != "ok") {
      throw gfError(xhr.status, js.status);
    }
    let newID = js.data?.id;
    // Gofile adds a second file with the name. The one that was there goes
    // only after the new one is stored, as in rclone.
    if (target.old && newID && target.old.id != newID) {
      await this.remove(target.old.id!);
    }
  }

  async remove(id: string): Promise<void> {
    let js = await gfCall(this.token, "DELETE", "/contents", { contentsId: id });
    // each id has its own result
    for (let r of Object.values(js.data || {}) as any[]) {
      let status = r?.status;
      if (typeof status == "string" && status != "ok") {
        throw gfError(200, status);
      }
    }
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let isDir = path.endsWith("/");
    let from = splitPath(path);
    let to = splitPath(newPath);
    throwIf(from.length == 0 || to.length == 0, "cannot rename the root folder");
    let fromName = from[from.length - 1];
    let toName = to[to.length - 1];
    gofileCheckName(toName);
    throwIf(isDir && (dirKey(to) + "/").startsWith(dirKey(from) + "/"), "cannot move a folder into itself");
    let sameParent = dirKey(from.slice(0, -1)) == dirKey(to.slice(0, -1));

    try {
      await this.fresh(async () => {
        let src = await this.parentOf(from, false);
        let dst = sameParent ? src : await this.parentOf(to, false);
        let id = findItem(src.items, fromName, isDir)?.id;
        throwIf(!id, `not found: ${path}`);
        // Gofile would take a second entry with the name: we do not
        let taken = dst.items.find((it) => it.name == toName && it.id != id);
        throwIf(!!taken, `${toName} already exists`);

        if (fromName != toName) {
          await gfCall(this.token, "PUT", `/contents/${enc(id!)}/update`, {
            attribute: "name",
            attributeValue: toName,
          });
        }
        if (!sameParent) {
          let js = await gfCall(this.token, "PUT", "/contents/move", { folderId: dst.id, contentsId: id });
          let status = js.data?.[id!]?.status;
          if (typeof status == "string" && status != "ok") {
            throw gfError(200, status);
          }
        }
      });
    } finally {
      if (isDir) {
        // ids stay but the paths we remember them by changed
        this.dirIDs.clear();
      }
    }
  }

  async createDirectory(dirPath: string): Promise<void> {
    let parts = splitPath(dirPath);
    throwIf(parts.length == 0, "missing directory name");
    // so that its parent is asked, not our memory
    this.dirIDs.delete(dirKey(parts));
    await this.fresh(() => this.resolveDir(parts, true));
  }

  // There is no trash. A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    let parts = splitPath(path);
    if (!path.endsWith("/")) {
      let f = await this.findFile(path);
      await this.remove(f.id!);
      return;
    }
    throwIf(parts.length == 0, "cannot delete the root folder");
    // not from memory: what we remove must be the folder at this path now
    this.dirIDs.clear();
    let parent = await this.parentOf(parts, false);
    let f = findItem(parent.items, parts[parts.length - 1], true);
    throwIf(!f, `folder not found: ${path}`);
    try {
      await this.remove(f!.id!);
    } finally {
      this.dirIDs.clear();
    }
  }
}

function seconds(v: number | undefined): number {
  let n = Number(v || 0);
  return Number.isFinite(n) && n > 0 ? n * 1000 : 0;
}

export function gofileItemsToWire(items: GofileItem[]): any[][] {
  let wires: any[][] = [];
  for (let it of items) {
    if (!it.id || !it.name) {
      continue;
    }
    let lastMod = seconds(it.modTime) || seconds(it.createTime);
    if (isFolder(it)) {
      wires.push([it.id, it.name, -1, lastMod, []]);
      continue;
    }
    if (it.type != "file") {
      continue;
    }
    let size = Number(it.size || 0);
    let meta: any[] = [it.id, it.name, Number.isFinite(size) ? size : 0, lastMod, null];
    if (it.md5) {
      meta.push(prop_gofile_md5, it.md5);
    }
    if (it.mimetype) {
      meta.push(prop_gofile_mime_type, it.mimetype);
    }
    wires.push(meta);
  }
  return wires;
}

/**
 * Checks the token and finds the root folder of its account.
 */
export async function gofileConnect(token: string): Promise<GofileAccount> {
  let js = await gfCall(token, "GET", "/accounts/getid");
  let accountID = js.data?.id;
  throwIf(!accountID, "Gofile did not return the account");
  js = await gfCall(token, "GET", `/accounts/${enc(accountID)}`);
  let rootFolderID = js.data?.rootFolder;
  throwIf(!rootFolderID, "Gofile did not return the root folder");
  return { rootFolderID: rootFolderID as string, email: (js.data?.email as string) || "" };
}

function parseJSON(text: string): any {
  try {
    let js = JSON.parse(text);
    return js && typeof js == "object" ? js : {};
  } catch {
    return {};
  }
}

function gfError(httpStatus: number, code: any): GofileError {
  let c = typeof code == "string" && code != "" && code != "ok" ? code : "";
  if (c == "error-wrongToken" || c == "error-auth" || httpStatus == 401) {
    return new GofileError(c, "Gofile refused the token");
  }
  if (c == "error-notPremium") {
    return new GofileError(c, "Gofile allows this for premium accounts only");
  }
  if (c == "error-rateLimit" || httpStatus == 429) {
    return new GofileError(c, "Gofile: too many requests, try again in a while");
  }
  if (c == "error-notFound" || httpStatus == 404) {
    return new GofileError("error-notFound", "Gofile: not found");
  }
  return new GofileError(c, c ? `Gofile: ${c}` : `Gofile ${httpStatus}`);
}

async function gfCall(token: string, method: string, path: string, body?: object): Promise<any> {
  let headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  let init: RequestInit = { method, headers };
  if (body) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let rsp: Response;
  try {
    rsp = await fetch(gofileAPI + path, init);
  } catch {
    throw new Error("could not connect to Gofile");
  }
  let js = parseJSON(await rsp.text());
  if (!rsp.ok || js.status != "ok") {
    throw gfError(rsp.status, js.status);
  }
  return js;
}

// XMLHttpRequest because fetch does not report how much was sent or received.
function gfSend(
  method: string,
  url: string,
  token: string,
  body: FormData | null,
  responseType: "blob" | "text",
  onProgress: (loaded: number, total: number, durMs: number) => void,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let started = Date.now();
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.responseType = responseType;
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      let code = responseType == "text" ? parseJSON(xhr.responseText).status : "";
      reject(gfError(xhr.status, code));
    };
    xhr.onerror = () => {
      reject(new Error(body ? "upload to Gofile failed" : "download from Gofile failed"));
    };
    let report = (ev: ProgressEvent) => {
      onProgress(ev.loaded, ev.lengthComputable ? ev.total : 0, Date.now() - started);
    };
    if (body) {
      xhr.upload.onprogress = report;
    } else {
      xhr.onprogress = report;
    }
    xhr.send(body);
  });
}

export function makeFileSystemGofile(c: GofileConfig): FileSystemGofile {
  return new FileSystemGofile(c);
}
